/**
 * Endpoints para gestión de productos (ingredientes nutricionales) usando localStorage
 * Modo demo sin backend
 */
import { storage, STORAGE_KEYS, delay } from "../config";

// Generar ID único
const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

/**
 * Listar productos
 */
export const list = async (page = 1, filters = {}) => {
  await delay(200);
  let products = storage.get(STORAGE_KEYS.PRODUCTS, []);
  
  // Aplicar filtros
  if (filters.branch_id) {
    // Normalizar branch_id para comparación (convertir a string para comparación consistente)
    const branchIdStr = String(filters.branch_id);
    products = products.filter((p) => {
      const productBranch = p.branch?.id || p.branch;
      return String(productBranch) === branchIdStr;
    });
  }
  
  if (filters.is_nutritional_ingredient !== undefined) {
    products = products.filter((p) => p.is_nutritional_ingredient === filters.is_nutritional_ingredient);
  }
  
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    products = products.filter(
      (p) =>
        p.name?.toLowerCase().includes(searchLower) ||
        p.supplier_company?.toLowerCase().includes(searchLower)
    );
  }
  
  // Simular paginación
  const pageSize = 50;
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  const results = products.slice(start, end);
  
  return {
    results,
    count: products.length,
    page,
    page_size: pageSize,
  };
};

/**
 * Obtener un producto por ID
 */
export const retrieve = async (id) => {
  await delay(200);
  const products = storage.get(STORAGE_KEYS.PRODUCTS, []);
  const product = products.find((p) => p.id === id);
  
  if (!product) {
    throw new Error("Producto no encontrado");
  }
  
  return product;
};

/**
 * Crear un nuevo producto
 */
export const create = async (data) => {
  await delay(300);
  const products = storage.get(STORAGE_KEYS.PRODUCTS, []);
  
  const newProduct = {
    id: generateId(),
    ...data,
    created: new Date().toISOString(),
    modified: new Date().toISOString(),
  };
  
  products.push(newProduct);
  
  // Guardar y verificar que se guardó correctamente
  try {
    storage.set(STORAGE_KEYS.PRODUCTS, products);
    
    // Verificar que el producto se guardó correctamente
    const savedProducts = storage.get(STORAGE_KEYS.PRODUCTS, []);
    const savedProduct = savedProducts.find((p) => p.id === newProduct.id);
    
    if (!savedProduct) {
      throw new Error("El producto no se guardó correctamente en localStorage");
    }
    
    return newProduct;
  } catch (error) {
    console.error("Error al guardar producto:", error);
    throw error;
  }
};

/**
 * Actualizar un producto
 */
export const update = async (id, data) => {
  await delay(300);
  const products = storage.get(STORAGE_KEYS.PRODUCTS, []);
  const index = products.findIndex((p) => p.id === id);
  
  if (index === -1) {
    throw new Error("Producto no encontrado");
  }
  
  products[index] = {
    ...products[index],
    ...data,
    modified: new Date().toISOString(),
  };
  
  // Guardar y verificar que se guardó correctamente
  try {
    storage.set(STORAGE_KEYS.PRODUCTS, products);
    
    // Verificar que el producto se actualizó correctamente
    const savedProducts = storage.get(STORAGE_KEYS.PRODUCTS, []);
    const savedProduct = savedProducts.find((p) => p.id === id);
    
    if (!savedProduct) {
      throw new Error("El producto no se actualizó correctamente en localStorage");
    }
    
    return products[index];
  } catch (error) {
    console.error("Error al actualizar producto:", error);
    throw error;
  }
};

/**
 * Eliminar un producto
 */
export const destroy = async (id) => {
  await delay(200);
  const products = storage.get(STORAGE_KEYS.PRODUCTS, []);
  const filtered = products.filter((p) => p.id !== id);
  
  // Guardar y verificar que se eliminó correctamente
  try {
    storage.set(STORAGE_KEYS.PRODUCTS, filtered);
    
    // Verificar que el producto se eliminó correctamente
    const savedProducts = storage.get(STORAGE_KEYS.PRODUCTS, []);
    const deletedProduct = savedProducts.find((p) => p.id === id);
    
    if (deletedProduct) {
      throw new Error("El producto no se eliminó correctamente de localStorage");
    }
    
    return { success: true };
  } catch (error) {
    console.error("Error al eliminar producto:", error);
    throw error;
  }
};
